import React from 'react';
import { trackEvent } from 'ddc-track-event';
import { usePrefs, useLabels, useRequestData } from 'wsm-common-data';

/**
 * Shared "Show More" paging for the model-card layout containers.
 *
 * Encapsulates the initial `initialVehicleDisplay` slice, the reveal-all
 * handler (with its analytics event), the localized button label, and the
 * scroll-into-view focus effect so the grid (`col-*`) and the responsive
 * (container-aware) layouts share one implementation instead of duplicating
 * paging logic. Each container decides *whether* to render the button from
 * its own visibility rules; this hook owns *what* the button does.
 *
 * @param {Array<object>} vehicles - full ordered vehicle list to page through.
 * @returns {{
 *   vehiclesToDisplay: Array<object>,
 *   hasMore: boolean,
 *   buttonLabel: string,
 *   showMore: () => void
 * }}
 */
export const useShowMore = (vehicles) => {
	const labels = useLabels();
	const { initialVehicleDisplay } = usePrefs();
	const { widgetName, windowId } = useRequestData();

	const [vehiclesToDisplay, setVehiclesToDisplay] = React.useState(
		vehicles.slice(0, initialVehicleDisplay)
	);

	const hasMore = vehiclesToDisplay.length < vehicles.length;

	const buttonLabel = labels
		.get('SHOW_X_MORE')
		.replace('{count}', vehicles.length - vehiclesToDisplay.length)
		.toLowerCase();

	const showMore = () => {
		setVehiclesToDisplay(vehicles);
		trackEvent(widgetName, windowId, {
			action: 'clicked',
			element: 'View More Link',
			elementCTA: buttonLabel,
			result: 'Displayed more Models'
		});
	};

	React.useEffect(() => {
		const elementToFocus = document.getElementById(
			`card-${initialVehicleDisplay}`
		);
		if (elementToFocus) {
			setTimeout(() => {
				elementToFocus?.scrollIntoView({
					behavior: 'smooth',
					block: 'start'
				});
			}, 200);
		}
	}, [vehiclesToDisplay, initialVehicleDisplay]);

	return { vehiclesToDisplay, hasMore, buttonLabel, showMore };
};
